import {
    AlphaAttribute,
    ColorAttribute,
    getAttributesToSerialize,
    NumberAttribute,
} from '../../../attributes';
import { Config } from '../../../../video-data/config';
import type { CanvasTextDynamicCircleRendererSettings } from '../renderer/CanvasTextDynamicCircleRenderer';
import { CanvasTextDynamicCircleRenderer } from '../renderer/CanvasTextDynamicCircleRenderer';
import type { Pixels } from '../../../../types';
import { int2rgba } from '../../../../libs/videoData';
import { DynamicXAlign, DynamicYAlign } from '../../../../enums';
import type { LoadSettings } from '../../../serializableMixin';
import { cloneDeep } from '../../../../libs/cloneDeep';
import { TextAssetDynamicGraphic } from './TextAssetDynamicGraphic';

/**
 * CanvasTextAssetDynamicCircle is the Luminary equivalent of CanvasTextDynamicCircle and provides
 * a bridge between Luminary and the CanvasText library.
 */
export class TextAssetDynamicCircle extends TextAssetDynamicGraphic {
    override _classType = 'CanvasTextDynamicCircle';

    override readonly type = Config.CIRCLE_GRAPHIC;
    @NumberAttribute({ min: 0, max: Infinity })
    accessor radius = 0;
    @ColorAttribute()
    accessor strokeColor = 0xffffff;
    @AlphaAttribute()
    accessor strokeAlpha = 1;
    @NumberAttribute({ min: 0, max: Infinity })
    accessor strokeWidth = 0;
    @ColorAttribute()
    accessor fill = 0xff00ff;
    @AlphaAttribute()
    accessor fillAlpha = 0.5;

    override getCanvasRendererSettings(): CanvasTextDynamicCircleRendererSettings {
        const base = super.getCanvasRendererSettings();

        // make the stroke invisible for zero width
        const alpha = this.strokeWidth > 0 ? this.strokeAlpha : 0;
        const style = int2rgba(this.strokeColor, alpha);

        return {
            ...base,
            fill: int2rgba(this.fill, this.fillAlpha),
            type: this.type,
            radius: (this.radius * this.referenceToOutputMultiplier) as Pixels,
            strokeStyle: style,
            lineWidth: (this.strokeWidth * this.referenceToOutputMultiplier) as Pixels,
        };
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(getAttributesToSerialize(this));
    }

    /* The list of properties that can be animated during draw */
    override get animatableProperties() {
        // Since CanvasTextDynamicCircle is responsible for rendering our text we need to ask it which properties it
        // supports. See its rendering logic for more details.
        return CanvasTextDynamicCircleRenderer.animatableProperties;
    }

    override get animatableGeometricProperties() {
        return CanvasTextDynamicCircleRenderer.animatableGeometricProperties;
    }

    override get editableProperties() {
        return super.editableProperties.concat(['fill', 'fillAlpha', 'strokeColor', 'strokeAlpha']);
    }

    override loadTextAssetStyle(data, settings: LoadSettings) {
        const newData = cloneDeep(data);
        // There was a bug in the old system where even though circles were drawn from the center, the alignment logic
        // was assuming they were drawn from the top left. This meant designers were adding a position offset to
        // compensate for this. We need to remove this offset for the canvas renderer since it aligns & draws circles
        // from the center.
        if (data.alignGraphicX === DynamicXAlign.center) {
            newData.shape.positionX -= data.shape.radius;
        }
        if (data.alignGraphicY === DynamicYAlign.center) {
            newData.shape.positionY -= data.shape.radius;
        }

        if (data.shape.radius !== undefined) {
            newData.radius = data.shape.radius;
        }
        if (data.shape.strokeColor !== undefined) {
            newData.strokeColor = data.shape.strokeColor;
        }
        if (data.shape.strokeAlpha !== undefined) {
            newData.strokeAlpha = data.shape.strokeAlpha;
        }
        if (data.shape.strokeWidth !== undefined) {
            newData.strokeWidth = data.shape.strokeWidth;
        }
        if (data.shape.fillColor !== undefined) {
            newData.fill = data.shape.fillColor;
        }
        if (data.shape.fillAlpha !== undefined) {
            newData.fillAlpha = data.shape.fillAlpha;
        }

        // old text asset data contains corrupted animation configs
        newData.animation = newData.animation.filter((animation) => {
            if (animation.property === 'progressXValue') {
                return false;
            }
            return true;
        });

        super.loadTextAssetStyle(newData, settings);
    }
}
